import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CategoryType } from '@core/models/category-type.model';
import { ContractNatioNegoCondition } from '@core/models/contract-natio-nego-condition.model';
import { Contract } from '@core/models/contract.model';
import { FilteredItem } from '@core/models/filtered-item.model';
import { NegotiationType } from '@core/models/negotiation-type.model';
import { ContractCategoryTypeService } from '@core/services/contract-category-type/contract-category-type.service';
import { TvSelectItemsDialogComponent } from '@shared/dialog/tv-select-items-dialog/tv-select-items-dialog.component';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
@Component({
  standalone: false,
  selector: 'app-natio-nego-condition-dynamic',
  templateUrl: './natio-nego-condition-dynamic.component.html',
  styleUrls: ['./natio-nego-condition-dynamic.component.scss'],
})
export class NatioNegoConditionDynamicComponent implements OnInit, OnDestroy {
  public isItemLoading: boolean = false;
  public categoryTypeFormControl = new FormControl(null);
  public searchFormControl = new FormControl(null);

  @Input() groupId: number;
  @Input() contract: Contract;
  @Input() conditionType?: { value: number; label: string };
  @Input() categoryTypeList?: CategoryType[] = [];
  @Input() groupedConditions: ContractNatioNegoCondition[];
  @Input() isDuplication: boolean = false;

  public itemList: any[] = [];
  public filteredItemList: Observable<any[]>;
  public chosenItemList: any[] = [];
  private readonly itemAutocompleteLimit = 100;
  private loadingCategoryTypeId: number | null = null;

  @Output() messageEvent = new EventEmitter<string>();

  componentDestroyed$: Subject<void> = new Subject();

  constructor(public contractCategoryTypeService: ContractCategoryTypeService, private snackBar: MatSnackBar, public dialog: MatDialog) {}

  ngOnInit(): void {
    this.handleCategoryTypeValueChange();
    this.handleItemValueChange();
    this.initDialog(); // load values for edit
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  initDialog() {
    if (this.groupedConditions && this.groupedConditions.length > 0) {
      this.groupedConditions.forEach(condition => {
        this.chosenItemList.push({
          id: condition.id,
          itemType: new CategoryType(condition.categoryType),
          item: { id: condition.categoryValue?.idCategoryValue, name: condition.categoryValue?.labelCategoryValue },
        });
      });
    }
  }

  public initFilteredItemList() {
    if (!this.isItemLoading) {
      this.filteredItemList = this.searchFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => this.getItemAutocompleteOptions(label))
      );
    }
  }

  /**
   *  Fonction pour gérer les cas de sélection d'option ou de chaîne tapée
   * @param value
   * @private
   */
  private _getLabel(value: string | NegotiationType): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  /***
   * Fonction pour afficher le label dans l'input lors de la sélection
   * @param value
   * @private
   */
  private _filter(value: string = ''): any[] {
    const filterValue = value.toLowerCase();
    return this.itemList
      .filter(item => item.id?.toString().includes(filterValue) || item.name?.toLowerCase().includes(filterValue))
      .slice(0, this.itemAutocompleteLimit);
  }

  public displayItem(item: FilteredItem): string {
    return item?.name && item?.id ? `${item.name} (${item.id})` : '';
  }

  public trackByItemId(index: number, item: any): any {
    return item?.id;
  }

  private getItemAutocompleteOptions(label: string): any[] {
    return label ? this._filter(label) : this.itemList.slice(0, this.itemAutocompleteLimit);
  }

  private handleCategoryTypeValueChange() {
    this.categoryTypeFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.prepareItemListLoading(value);
      this.getCategoryItemList(value);
    });
  }

  private prepareItemListLoading(categoryType: CategoryType | null): void {
    this.loadingCategoryTypeId = categoryType?.id ?? null;
    this.itemList = [];
    this.filteredItemList = of([]);
    this.searchFormControl.setValue('', { emitEvent: false });

    if (categoryType) {
      this.isItemLoading = true;
      this.searchFormControl.disable({ emitEvent: false });
    } else {
      this.isItemLoading = false;
      this.searchFormControl.enable({ emitEvent: false });
    }
  }

  public getParamsByCategoryType(categoryType) {
    switch (categoryType.id) {
      case 1: // Advertiser
      case 4: // Product
        return { advertiserGroupId: this.contract.advertiserId };
      case 6: // Campaign
        return { advertiserGroupId: this.contract.advertiserId, year: this.contract.cgvContractYear };
      case 3: // Secodip
        return { year: this.contract.cgvContractYear };
      case 2: // Mandatory / Agency
      case 5: // Product Type
      case 7: // Target
      case 8: // Channel
      case 9: // Screen Type
      case 10: // Screen
      case 11: // Time Slot
      default:
        return {};
    }
  }

  public getCategoryItemList(categoryType) {
    if (!categoryType) return;

    const categoryTypeId = categoryType.id;
    let params = this.getParamsByCategoryType(categoryType);

    this.contractCategoryTypeService
      .getItemList(params, categoryType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        items => {
          if (this.loadingCategoryTypeId !== categoryTypeId) return;

          this.isItemLoading = false;
          this.itemList = items;
          this.searchFormControl.enable({ emitEvent: false });
          this.initFilteredItemList();
        },
        error => {
          if (this.loadingCategoryTypeId !== categoryTypeId) return;

          this.isItemLoading = false;
          this.searchFormControl.enable({ emitEvent: false });
        }
      );
  }

  private handleItemValueChange() {
    this.searchFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      if (typeof value !== 'string') {
        if (!this.chosenItemList.find(chosenItem => chosenItem.item.id === value.id && chosenItem.item.name === value.name)) {
          this.chosenItemList.push({
            itemType: this.categoryTypeFormControl.value,
            item: value,
          });
        } else {
          this.snackBar.open('Cet item est déjà sélectionné.', null, {
            duration: 4000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-error'],
          });
        }
      }
    });
  }

  public isFilteredItemListFill() {
    return this.isItemLoading || !this.categoryTypeFormControl.value || !this.filteredItemList;
  }

  public listAllOptions(): void {
    if (this.isFilteredItemListFill()) return;

    const currentCategoryType = this.categoryTypeFormControl.value;

    const dialogRef = this.dialog.open(TvSelectItemsDialogComponent, {
      data: {
        itemList: of(this.itemList),
        itemListAlreadySelected: this.chosenItemList.filter(chosenItem => chosenItem.itemType?.id === currentCategoryType?.id),
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.afterClosed().subscribe(items => {
      if (!items) return;

      const selectedItems = Array.from(items as Set<any>);

      this.chosenItemList = [
        ...this.chosenItemList.filter(chosenItem => chosenItem.itemType?.id !== currentCategoryType?.id),
        ...selectedItems.map(item => {
          const existingItem = this.chosenItemList.find(
            chosenItem => chosenItem.itemType?.id === currentCategoryType?.id && chosenItem.item?.id === item.id
          );

          return {
            id: existingItem?.id || null,
            item: item,
            itemType: currentCategoryType,
          };
        }),
      ];
    });
  }

  removeItem(chip: string): void {
    const index = this.chosenItemList.indexOf(chip);
    if (index >= 0) {
      this.chosenItemList.splice(index, 1);
    }
  }

  removeCondition(): void {
    this.messageEvent.emit('remove');
  }

  public getCategoryTypeToDisplay(categoryType) {
    return categoryType?.name.substring(0, 2);
  }

  public getConditionList() {
    return this.chosenItemList.map(chosenItem => {
      return new ContractNatioNegoCondition({
        id: !this.isDuplication ? chosenItem.id : null,
        groupId: this.groupId,
        isInclude: this.conditionType.value,
        categoryType: chosenItem.itemType,
        categoryValue: {
          idCategoryValue: chosenItem.item.id,
          labelCategoryValue: chosenItem.item.name,
        },
      });
    });
  }
}
